import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { CofreBiometrico } from 'cofre-biometrico';
import { api } from '@/utils/api';
import { acrescentar, FRASE_DA_PROVA, FRASE_DO_LIMITE, podeGuardar, podeTrocar, remover, type ContaGuardada } from './regrasDasContas';

/**
 * AS CONTAS GUARDADAS NESTE APARELHO — o "Trocar de conta" (1.4, 23/10/2026; `docs/PLANO-TROCAR-DE-CONTA.md`). Só no app.
 *
 * - A LISTA (nome, papel, usuário, o id da credencial no servidor — NUNCA o segredo) mora em `@capacitor/preferences`
 *   (sobrevive ao fechar o app; o localStorage da WebView pode perder a última gravação).
 * - O SEGREDO de cada conta (a credencial do aparelho, `finalidade: "conta-guardada"`, 90 dias, revogável no servidor) mora
 *   no cofre, uma chave por conta (`conta:<id>`). Ler o cofre SEMPRE pede a biometria: toda troca pede Face ID/digital.
 * - A biometria do aparelho mudou → o sistema invalida o cofre inteiro → cada conta volta a pedir a senha (e sai da lista).
 */
/**
 * O COFRE E AS PREFERÊNCIAS DE TESTE: só no build da web de TESTE (`VITE_COFRE_DE_TESTE=1`, posto pelo roteiro das telas),
 * a página pode trocar os dois por versões de mentira (`window.__cofreDeTeste`, `window.__preferenciasDeTeste`) — no
 * navegador não há cofre nem ponte nativa. No build de produção a variável não existe, e o ramo some.
 */
type Cofre = typeof CofreBiometrico;
type Prefs = typeof Preferences;
const deTeste = import.meta.env.VITE_COFRE_DE_TESTE === '1';
const cofre = (): Cofre => (deTeste && (window as unknown as { __cofreDeTeste?: Cofre }).__cofreDeTeste) || CofreBiometrico;
const preferencias = (): Prefs => (deTeste && (window as unknown as { __preferenciasDeTeste?: Prefs }).__preferenciasDeTeste) || Preferences;

const LISTA = 'contas:guardadas';
export const ADICIONANDO = 'contas:adicionando';
const chaveDoCofre = (userId: number) => `conta:${userId}`;

export const contasDisponiveis = () => Capacitor.isNativePlatform();

export async function listarContas(): Promise<ContaGuardada[]> {
  if (!contasDisponiveis()) return [];
  try { const { value } = await preferencias().get({ key: LISTA }); return value ? (JSON.parse(value) as ContaGuardada[]) : []; } catch { return []; }
}
async function gravar(lista: ContaGuardada[]) { await preferencias().set({ key: LISTA, value: JSON.stringify(lista) }); }

/** Guarda a conta da sessão aberta (que precisa ter sido aberta com a senha ou o Google). */
export async function guardarContaAtual(u: { id: string | number; username: string; name: string; role: string }): Promise<void> {
  if (!podeGuardar(u)) throw new Error('Guardar a conta para trocar é só da equipe (professor, gestão e administração).');
  const lista = await listarContas();
  const prevista = acrescentar(lista, { userId: Number(u.id), username: u.username, nome: u.name, papel: u.role, credencialId: null, guardadaEm: new Date().toISOString() });
  if (prevista.erro === 'LIMITE') throw new Error(FRASE_DO_LIMITE);
  let dados: { credencial: string; id: number };
  try {
    dados = (await api.post('/api/auth/credencial-do-aparelho', { finalidade: 'conta-guardada', plataforma: Capacitor.getPlatform() })).data;
  } catch (e) {
    const st = (e as { response?: { status?: number } }).response?.status;
    throw new Error(st === 403 ? 'Para guardar esta conta, entre nela com a senha (a entrada pela biometria não guarda outra).' : 'Sem conexão com o portal agora. Tente de novo.');
  }
  try { await cofre().guardar({ chave: chaveDoCofre(Number(u.id)), valor: dados.credencial }); }
  catch { throw new Error('Não foi possível usar a biometria deste aparelho. Cadastre uma digital ou o Face ID nas configurações do celular.'); }
  const final = acrescentar(await listarContas(), { userId: Number(u.id), username: u.username, nome: u.name, papel: u.role, credencialId: dados.id, guardadaEm: new Date().toISOString() });
  await gravar(final.lista);
}

/** Troca para a conta guardada: prova no aparelho primeiro, depois a biometria, depois o servidor. Devolve a sessão, ou null (cancelou). */
export async function trocarPara(conta: ContaGuardada, atualId: number | null): Promise<{ token: string; user: never } | null> {
  const { conferirCopiasDoAparelho } = await import('@/features/aplicativo/trocaNaAbertura');
  const { provaGuardada } = await conferirCopiasDoAparelho();
  const motivo = podeTrocar({ provaGuardada, mesmaConta: atualId === conta.userId });
  if (motivo === 'PROVA') throw new Error(FRASE_DA_PROVA);
  if (motivo === 'MESMA') return null;
  let credencial: string;
  try {
    credencial = (await cofre().ler({ chave: chaveDoCofre(conta.userId), titulo: `Entrar como ${conta.nome}`, botaoSenha: 'Cancelar' })).valor;
  } catch (e) {
    const code = (e as { code?: string }).code;
    if (code === 'CANCELADO' || code === 'USAR_SENHA') return null;
    if (code === 'BIOMETRIA_MUDOU' || code === 'SEM_SEGREDO') { await esquecer(conta.userId); throw new Error('A biometria deste aparelho mudou. Entre nesta conta com a senha para guardá-la de novo.'); }
    throw new Error('Não foi possível usar a biometria agora.');
  }
  try {
    return (await api.post('/api/auth/entrar-com-aparelho', { credencial })).data;
  } catch (e) {
    if ((e as { response?: { status?: number } }).response?.status === 401) { await esquecer(conta.userId); throw new Error('O acesso guardado desta conta não vale mais (revogado, a senha mudou ou passaram 90 dias). Entre com a senha.'); }
    throw new Error('Sem conexão com o portal agora. Tente de novo.');
  }
}

/** Tira a conta do aparelho (a lista e o cofre). E o aparelho deixa de receber os avisos dela no celular (1.4.1). */
export async function esquecer(userId: number): Promise<void> {
  await cofre().apagar({ chave: chaveDoCofre(userId) }).catch(() => undefined);
  await gravar(remover(await listarContas(), userId));
  const { removerContaDoPush } = await import('@/features/notificacoes/pushDoAparelho');
  await removerContaDoPush(userId);
}

/** "Sair desta conta" de uma conta guardada que NÃO é a aberta: a credencial (lida com a biometria) se revoga no servidor. */
export async function sairDaContaGuardada(conta: ContaGuardada): Promise<{ revogadaNoServidor: boolean }> {
  let revogada = false;
  try {
    const credencial = (await cofre().ler({ chave: chaveDoCofre(conta.userId), titulo: `Sair de ${conta.nome} neste aparelho`, botaoSenha: 'Cancelar' })).valor;
    revogada = !!(await api.post('/api/auth/credencial-do-aparelho/revogar', { credencial })).data?.revogada;
  } catch { /* sem biometria ou sem rede: sai só do aparelho; a credencial vence em 90 dias, ou se revoga em Minha conta */ }
  await esquecer(conta.userId);
  return { revogadaNoServidor: revogada };
}

/**
 * A conta ABERTA sai deste aparelho (chamado pelo "Sair"): revoga pela sessão e esquece. A sessão vem de quem chama, lida
 * ANTES de o "Sair" apagá-la — o pedido sai depois, e sem ela o servidor responderia 401.
 */
export async function sairDaContaAtual(userId: number | string | undefined, sessao: string | null): Promise<void> {
  if (!contasDisponiveis() || userId == null) return;
  const conta = (await listarContas()).find((c) => c.userId === Number(userId));
  if (!conta) return;
  if (conta.credencialId && sessao) await api.post(`/api/auth/aparelhos/${conta.credencialId}/revogar`, {}, { headers: { Authorization: `Bearer ${sessao}` } }).catch(() => undefined);
  await esquecer(conta.userId);
}
